import { FieldSelect, IconButton } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { ampli } from 'lib-shared-tracking';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import type { Action, Dispatch } from 'redux';
import { change, Field } from 'redux-form';

import type { FlowCategoriesT } from 'components/powerFlowShared/helpers/flowVariableHelpers';
import type { FieldRepeaterRowProps } from 'fable/fieldRepeater/fieldRepeater';
import { FlowType } from 'fable/flowEditor';

import { MATCH_BOOLEAN_DEFAULT_VALUE } from '../../constants';
import {
  MatchingOperator,
  MatchingSystemType,
  MatchValueT,
  VariableScope,
} from '../../nodeTypes/types';
import type { MatchingRuleT } from '../../nodeTypes/types';

import { ConditionVarSelector } from './conditionVariableSelector';
import {
  getMatchComponent,
  getOperatorOptions,
} from './fieldConditionComponent/';
import styles from './fieldConditionComponent.module.scss';

type DispatchPropsT = {
  changeMatchVariable: (matchVariable: MatchValueT) => void;
  changeOperator: (operator: string) => void;
};
type PropsT = DispatchPropsT &
  FieldRepeaterRowProps<{
    rules: MatchingRuleT[];
    variables: FlowCategoriesT;
  }>;

const BOOLEAN_VARIABLE_TYPES = [
  MatchingSystemType.AGENT_HAS_REPLIED,
  MatchingSystemType.AGENT_HAS_REPLIED_TRIGGER,
  MatchingSystemType.USER_HAS_REPLIED,
  MatchingSystemType.USER_HAS_REPLIED_TRIGGER,
];

class FieldConditionComponentImpl extends PureComponent<PropsT> {
  componentDidUpdate(prevProps: PropsT) {
    const { changeMatchVariable, changeOperator, rules, index } = this.props;
    const { rules: prevRules, index: prevIndex } = prevProps;
    const currentRule = rules?.[index];
    const prevRule = prevRules?.[prevIndex];
    const currentOptions = getOperatorOptions(currentRule.variable);
    const currentOperatorExists = currentOptions.find(
      (opt) => opt.id === currentRule.operator,
    );

    // If variable was changed and the selected operator is not supported,
    // then change operator to one that is supported
    if (!currentOperatorExists && currentOptions[0]) {
      changeOperator(currentOptions[0].id);
    }

    const prevVariableType = prevRule.variable?.variableType;
    const variableType = currentRule.variable?.variableType;

    // variableType changes reset to default matchValues
    if (variableType && prevVariableType !== variableType) {
      changeMatchVariable({
        scope: VariableScope.INPUT,
        values: (BOOLEAN_VARIABLE_TYPES as string[]).includes(variableType)
          ? [MATCH_BOOLEAN_DEFAULT_VALUE]
          : [],
      });
    }
  }

  @boundMethod
  handleRemoveRow() {
    const { onRemove, index } = this.props;
    ampli.flowActionConditionRemoved({
      flowType: FlowType.FLOW_TYPE_AUTOMATION,
    });
    onRemove(index);
  }

  render() {
    const { field, totalRows, index, variables, rules } = this.props;

    const currentRule = rules?.[index];
    const showMatchField = ![
      MatchingOperator.IS_EMPTY,
      MatchingOperator.IS_NOT_EMPTY,
    ].includes(currentRule.operator);

    const MatchComponent = getMatchComponent(currentRule.variable);
    return (
      <div className={styles.rulesRow}>
        <div className={styles.ruleInputs}>
          <div className={styles.ruleInputsFirst}>
            <Field
              component={ConditionVarSelector}
              name={`${field}.variable`}
              className={styles.ruleInputVariable}
              required
              placeholder="Select variable"
              variables={variables}
            />

            <Field
              name={`${field}.operator`}
              component={FieldSelect}
              className={styles.ruleInputMatchingrule}
              required
              items={getOperatorOptions(currentRule.variable)}
              dropdownProps={{
                fixed: true,
              }}
              noLabel
              noAlert
            />
          </div>
          {showMatchField && MatchComponent && (
            <div className={styles.ruleInputsSecond}>
              <Field
                component={MatchComponent}
                name={`${field}.match`}
                className={styles.ruleInputMatch}
                required
                placeholder="Input or variable"
                variables={variables}
                allowFreetext
              />
            </div>
          )}
        </div>
        <div className={styles.ruleActions}>
          <IconButton
            icon="trash"
            variant="danger"
            disabled={totalRows === 1}
            onClick={this.handleRemoveRow}
          />
        </div>
      </div>
    );
  }
}

export const FieldConditionComponent = connect(
  undefined,
  (dispatch: Dispatch<Action>, ownProps: PropsT) => {
    const { field, meta } = ownProps;
    const matchFieldName = `${field}.match`;
    const operatorFieldName = `${field}.operator`;
    return {
      changeMatchVariable: (matchVariable: MatchValueT) =>
        dispatch(change(meta.form, matchFieldName, matchVariable)),
      changeOperator: (operatorValue: string) =>
        dispatch(change(meta.form, operatorFieldName, operatorValue)),
    };
  },
)(FieldConditionComponentImpl);
